import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ChevronDown, ChevronUp, GraduationCap, Search, MessageCircle, ArrowRight, ShieldCheck, Smartphone } from "lucide-react";
import SEO from "../components/SEO";
import { SOCIAL } from "../data/siteData";
import PageHeader from "../components/PageHeader";
import Breadcrumbs from "../components/Breadcrumbs";
import { BECOME_TUTOR_FAQS, BECOME_TUTOR_FAQ_CATEGORIES } from "../data/becomeTutorFaqs";
import { trackCTA } from "../lib/analytics";

const SITE_URL = "https://anandtutorials.com";

// Build FAQPage JSON-LD for Google + AI search citation
function buildFaqSchema(faqs) {
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
    };
}

export default function BecomeTutorFAQs() {
    const [activeCat, setActiveCat] = useState("all");
    const [query, setQuery] = useState("");
    const [openIdx, setOpenIdx] = useState(new Set([0]));

    const filtered = useMemo(() => {
        let list = BECOME_TUTOR_FAQS;
        if (activeCat !== "all") list = list.filter((f) => f.c === activeCat);
        if (query.trim()) {
            const q = query.trim().toLowerCase();
            list = list.filter((f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q));
        }
        return list;
    }, [activeCat, query]);

    const toggle = (i) => {
        const next = new Set(openIdx);
        if (next.has(i)) next.delete(i); else next.add(i);
        setOpenIdx(next);
    };

    const faqSchema = useMemo(() => buildFaqSchema(BECOME_TUTOR_FAQS), []);

    return (
        <div className="bg-white" data-testid="become-tutor-faqs-page">
            <SEO
                title="Become a Tutor — FAQs | Anand Tutorials"
                description="Everything you need to know about becoming a 1:1 home & online tutor with Anand Tutorials — eligibility, registration (no joining fee), profile verification, subject interview, earnings, demo classes, assignment process, working hours, female tutor opportunities, app usage and more. 88 detailed FAQs."
                keywords="become a tutor faqs, tutor registration faqs, how to become a tutor, anand tutorials tutor faq, home tutor jobs faqs, online tutor jobs faqs, female tutor opportunities, tutor interview process, tutor earnings, tutor app, no joining fee tutor, tutor assignment process, college student tutor"
                path="/become-a-tutor-faqs"
            />
            <SEO schema={faqSchema} />

            <Helmet>
                <link rel="canonical" href={`${SITE_URL}/become-a-tutor-faqs`} />
            </Helmet>

            <PageHeader
                eyebrow="Tutor Onboarding · FAQs"
                title="Become a Tutor — Frequently Asked Questions"
                subtitle="88 detailed answers covering eligibility, registration (no joining fee), profile verification, subject interview, demo classes, earnings, assignment flow, app usage and more."
            />

            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
                <Breadcrumbs items={[
                    { name: "Become a Tutor", to: "/become-a-tutor" },
                    { name: "FAQs" },
                ]} />
            </div>

            {/* Trust strip */}
            <section className="bg-[#F8FAFC] border-y border-slate-100">
                <div className="max-w-6xl mx-auto px-6 lg:px-8 py-6 grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                    {[
                        ["88", "FAQs answered"],
                        ["FREE", "Joining fee"],
                        ["2,500+", "Active tutors"],
                        ["10-14 days", "Avg. onboarding"],
                    ].map(([v, l]) => (
                        <div key={l}>
                            <div className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A]">{v}</div>
                            <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{l}</div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="py-12 max-w-6xl mx-auto px-6 lg:px-8">
                <div className="grid lg:grid-cols-[260px_1fr] gap-8">
                    {/* Category nav */}
                    <aside className="lg:sticky lg:top-24 self-start">
                        <div className="space-y-1 max-h-[70vh] overflow-auto pr-2">
                            <button
                                onClick={() => setActiveCat("all")}
                                data-testid="faq-cat-all"
                                className={`block w-full text-left text-sm px-3 py-2 rounded-lg font-semibold ${activeCat === "all" ? "bg-[#1E3A8A] text-white" : "text-slate-700 hover:bg-slate-100"}`}
                            >
                                All ({BECOME_TUTOR_FAQS.length})
                            </button>
                            {BECOME_TUTOR_FAQ_CATEGORIES.map((cat) => {
                                const count = BECOME_TUTOR_FAQS.filter((f) => f.c === cat.id).length;
                                return (
                                    <button
                                        key={cat.id}
                                        onClick={() => setActiveCat(cat.id)}
                                        data-testid={`faq-cat-${cat.id}`}
                                        className={`block w-full text-left text-sm px-3 py-2 rounded-lg ${activeCat === cat.id ? "bg-[#1E3A8A] text-white font-semibold" : "text-slate-700 hover:bg-slate-100"}`}
                                    >
                                        {cat.label} <span className="text-xs opacity-70">({count})</span>
                                    </button>
                                );
                            })}
                        </div>
                    </aside>

                    {/* FAQ list */}
                    <div>
                        <div className="relative mb-6">
                            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                data-testid="faq-search"
                                placeholder="Search FAQs (e.g. earnings, demo, female tutor)…"
                                className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-300 focus:border-[#1E3A8A] focus:ring-2 focus:ring-blue-100 outline-none"
                            />
                        </div>

                        {filtered.length === 0 ? (
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-sm text-amber-800">
                                No FAQs matched your search. Try a different word.
                            </div>
                        ) : (
                            <ul className="space-y-2">
                                {filtered.map((f, i) => {
                                    const isOpen = openIdx.has(i);
                                    return (
                                        <li key={`${f.c}-${i}`} className="bg-white border border-slate-200 rounded-xl">
                                            <button
                                                onClick={() => toggle(i)}
                                                data-testid={`faq-q-${i}`}
                                                className="w-full text-left flex items-start justify-between gap-3 p-4 sm:p-5"
                                                aria-expanded={isOpen}
                                            >
                                                <span className="font-semibold text-[#0F172A]">{f.q}</span>
                                                {isOpen ? <ChevronUp className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" /> : <ChevronDown className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />}
                                            </button>
                                            {isOpen && (
                                                <div data-testid={`faq-a-${i}`} className="px-4 sm:px-5 pb-5 text-sm text-slate-700 leading-relaxed">{f.a}</div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            </section>

            {/* Sticky bottom CTA — apply / app download */}
            <section className="py-14 bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] text-white">
                <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300 mb-3 flex items-center justify-center gap-2">
                        <GraduationCap className="w-3 h-3" /> Start your tutor journey
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">No joining fee. No paperwork. Just real teaching.</h2>
                    <p className="text-blue-100 mb-7 max-w-2xl mx-auto">
                        Download the official Anand Tutorials tutor app, complete your profile, clear the subject interview, and start receiving 1:1 home and online tuition assignments — typically within 10-14 days.
                    </p>
                    <div className="flex flex-wrap justify-center gap-3">
                        <Link
                            to="/become-a-tutor"
                            onClick={() => trackCTA("become_tutor_apply_clicked", { cta_location: "faq-bottom-cta" })}
                            data-testid="faq-apply-cta"
                            className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-6 py-3 rounded-lg hover:bg-amber-400"
                        >
                            Apply as Tutor <ArrowRight className="w-4 h-4" />
                        </Link>
                        <a
                            href={`https://wa.me/${SOCIAL.whatsapp}?text=Hi%2C%20I%20would%20like%20to%20become%20a%20tutor%20with%20Anand%20Tutorials`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => trackCTA("become_tutor_whatsapp_clicked", { cta_location: "faq-bottom-cta" })}
                            data-testid="faq-whatsapp-cta"
                            className="inline-flex items-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] text-white font-semibold px-6 py-3 rounded-lg"
                        >
                            <MessageCircle className="w-4 h-4" /> WhatsApp Us
                        </a>
                    </div>
                    <div className="mt-6 text-sm text-blue-200 flex flex-wrap justify-center gap-4">
                        <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> 4-step verification</span>
                        <span className="inline-flex items-center gap-1.5"><Smartphone className="w-4 h-4" /> Apply via the tutor app</span>
                    </div>
                </div>
            </section>
        </div>
    );
}
